/**
 * Individual schedule line row component.
 *
 * Renders dynamic column cells based on visibility configuration,
 * with status badges, per-row action buttons, and selection checkbox.
 */
import { Check, XCircle, CalendarDays, Hash, Pause, Play, ExternalLink, CircleSlash } from 'lucide-react';
import { Link } from 'react-router-dom';
import PoLineLink from '../../../common/PoLineLink';
import PoHeaderLink from '../../../common/PoHeaderLink';
import ManualAgreementBadge from '../../ManualAgreementBadge';

import type { POLineSchedule } from '../../../../lib/api/types/purchasing';
import {
  ScheduleColumnConfig,
  ACK_STATUS_CONFIG,
  SCHEDULE_STATUS_CONFIG,
  describeExceptionReason, isCollabClosed } from './types';
import { mayProposeChange, mayReviewProposal } from '../../pendingProposal';
import { mayReleaseHold, SCHEDULE_CLOSED_RECEIPT_STATUSES, statusOf, type HoldSide } from '../../lineStatus';

interface ScheduleLineRowProps {
  schedule: POLineSchedule;
  visibleColumns: ScheduleColumnConfig[];
  formatDate: (date: string | Date) => string;
  formatNumber: (value: number) => string;
  onOpenModal: (kind: string, schedule: POLineSchedule) => void;
  onRowClick?: (scheduleId: number) => void;
  isSelected: boolean;
  canSelectRow: boolean;
  onToggleSelect: (id: number) => void;
  isSupplier: boolean;
  /** The viewer's side of the table, for the hold-release rule. */
  viewerSide: HoldSide | null;
}

/** Human-readable hint for raw ERP order-type codes (shown as a tooltip). */
const ERP_ORDER_TYPE_LABELS: Record<string, string> = {
  '1': 'Standard / spot PO',
  '4': 'Scheduling agreement',
};

/** A schedule is "closed" once it's fully shipped or cancelled — no further
 *  collaboration is meaningful and the row should render as a read-only record. */
function isClosed(s: POLineSchedule): boolean {
  return s.status === 'shipped' || s.status === 'cancelled';
}

/** Whether the viewer may close a manual-agreement remainder from the row.
 *  Customer side only (the server refuses a supplier); a closed line has
 *  nothing left to close. Mirrors `close_held` on the backend. */
function canCloseHeld(s: POLineSchedule, isSupplier: boolean): boolean {
  return !isSupplier && !!s.collab_hold && !isClosed(s);
}

/** Who may release a hold is ONE rule, in lineStatus.mayReleaseHold, shared
 *  with the PO-line grid, the line modal and the schedule modal, and mirroring
 *  the server's release_hold. This copy keyed off the PLACER alone, so it knew
 *  nothing about G7's nominee and offered Release to a side that had handed the
 *  hold over (S1, gate round 1). */

/** Whether a schedule row should show accept/decline actions.
 *  Mirrors ScheduleDetailModal.canShowWorkflowActions — a declined change
 *  hands the ball back to the side whose proposal was rejected. */
function canAct(s: POLineSchedule, isSupplier: boolean): boolean {
  if (isCollabClosed(s)) return false;
  // The supplier's answer to a NEW schedule row: an acknowledgement, not a
  // proposal review, so it is stated here rather than inside the shared rule.
  if (isSupplier && s.acknowledge_status === 'new') return true;
  // EXPEDITING-PARITY PR 2: one declined-change rule for all four surfaces
  // (pendingProposal.mayReviewProposal). This grain used to re-offer
  // Accept/Reject at `change_declined` to the side whose proposal was declined;
  // under Aaron's 17 Sep ruling a decline with a counter never reaches that
  // status, and one without closes the request — a fresh Propose is the only
  // way on. It also refuses a HELD row, which the server has since PR 1.
  return mayReviewProposal(s, isSupplier ? 'supplier' : 'customer');
}

/** Whether a schedule row should show the propose change action.
 *  Shares ScheduleDetailModal.canShowProposeChange's rule
 *  (pendingProposal.mayProposeChange) rather than restating it — after a
 *  decline the proposing side must still be able to re-propose a new date/qty
 *  from the row (customer review 2026-07-08: the icons vanished on Change
 *  Declined), and while a request is open it is answered, never proposed over.
 *
 *  This row used to list the OTHER side's `change_proposed_*` as proposable,
 *  which the modal never did and the server has always refused
 *  (`_assert_no_counter_over_pending`): the supplier was offered Propose Date
 *  / Propose Qty over a customer's live request and only learned it was
 *  impossible after filling the form and submitting. */
function canPropose(s: POLineSchedule, isSupplier: boolean): boolean {
  if (isCollabClosed(s)) return false;
  return mayProposeChange(s, isSupplier ? 'supplier' : 'customer');
}

function renderAckBadge(s: POLineSchedule) {
  // Closed schedules show a "Closed" ack badge regardless of the stored
  // acknowledge_status — the row is no longer actionable and the "New" pill
  // misleads the user into thinking a response is expected.
  if (isClosed(s)) {
    // The ERP snapshot reconcile records WHY it retired a slot on the stored
    // acknowledge status: 'cancelled' (nothing received) or 'closed' (receipts
    // kept). Show that distinction; everything else closed reads "Closed".
    const key = s.status === 'cancelled' && s.acknowledge_status === 'cancelled' ? 'cancelled' : 'closed';
    const config = ACK_STATUS_CONFIG[key] || { label: 'Closed', className: 'bg-gray-100 text-gray-500' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>{config.label}</span>;
  }
  // EXPEDITING-PARITY PR 2, Aaron 17 Sep 2026 — the SAME ruling the PO line
  // grain got, so the two grains cannot say different things about a held row:
  // *"the status IS On Hold (Customer/Supplier), it cannot be both on hold and
  // in motion."* This row carried an On Hold column of its own, which is the
  // second badge the ruling removes; `statusOf` answers the hold, and falls
  // through to this grain's own vocabulary otherwise.
  //
  // The hold sits ABOVE `missed` and BELOW `closed`, which is the precedence
  // the schedule EXPORT has always used and states its reason for
  // (`_ack_status_label`, and `test_on_hold_outranks_missed`): a hold is a real
  // state with an owner and an action attached — taking it off is the only move
  // available — while `missed` is derived for display. Nothing is lost: the
  // Delivery Status column beside this one still reads "Missed".
  if (s.on_hold) {
    const { label, className, title } = statusOf(s, ACK_STATUS_CONFIG, SCHEDULE_CLOSED_RECEIPT_STATUSES);
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`} title={title}>{label}</span>;
  }
  // A missed line shows "Missed" for the same reason a closed one shows
  // "Closed": its stored acknowledge_status is usually still 'new' (the ERP
  // never acknowledges), and a "New" pill on a line whose date has passed
  // without delivery reads as "awaiting a response" when the response is
  // already overdue. Display only; the stored acknowledge_status is untouched,
  // because the collab workflow, the writeback gate and the tab filters all key
  // off the real value.
  if (s.status === 'missed') {
    const missed = SCHEDULE_STATUS_CONFIG['missed'];
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${missed.className}`}>{missed.label}</span>;
  }
  const { label, className, title } = statusOf(s, ACK_STATUS_CONFIG, SCHEDULE_CLOSED_RECEIPT_STATUSES);
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`} title={title}>{label}</span>;
}

function renderStatusBadge(s: POLineSchedule) {
  const config = SCHEDULE_STATUS_CONFIG[s.status] || { label: s.status, className: 'bg-gray-100 text-gray-700' };
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>{config.label}</span>;
}

function renderWaitingOn(s: POLineSchedule) {
  if (!s.waiting_on) return <span className="text-muted-foreground text-xs">-</span>;
  const cls = s.waiting_on === 'Supplier' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800';
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{s.waiting_on}</span>;
}

/**
 * Renders a single schedule line table row with all visible columns.
 */
export function ScheduleLineRow({
  schedule: s,
  visibleColumns,
  formatDate,
  formatNumber,
  onOpenModal,
  onRowClick,
  isSelected,
  canSelectRow,
  onToggleSelect,
  isSupplier,
  viewerSide,
}: ScheduleLineRowProps) {
  const renderCell = (colId: string) => {
    switch (colId) {
      case 'acknowledge_status':
        return (
          <td key={colId} className="py-3 px-4">
            {/* Two chips, each on its own unbreakable line: the status pill
                must never wrap mid-text ("Changed" / "(Supplier)"), so it keeps
                its width and the second chip drops beneath it when needed. */}
            <div className="flex flex-wrap items-center gap-1">
              <span className="shrink-0 whitespace-nowrap">
                {s.is_forecast ? (
                  <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-medium" title={`Forecast — outside firm window (${s.firm_days ?? '?'} days)`}>
                    Forecast
                  </span>
                ) : (
                  renderAckBadge(s)
                )}
              </span>
              {/* Manual Agreement is a second FACT, not a second reading of the same
                  one: the line carries an agreed change the ERP has not sent
                  back, and auto-close skips it. Shown on open lines only — a
                  closed line's hold has been released. */}
              {!isClosed(s) && <ManualAgreementBadge schedule={s} compact className="shrink-0" />}
            </div>
          </td>
        );
      case 'action':
        return (
          <td key={colId} className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
              {canAct(s, isSupplier) && (
                <>
                  <button onClick={() => onOpenModal('accept', s)} className="p-1 hover:bg-green-50 rounded" title="Accept"><Check className="h-4 w-4 text-green-600" /></button>
                  <button onClick={() => onOpenModal('decline', s)} className="p-1 hover:bg-red-50 rounded" title="Decline"><XCircle className="h-4 w-4 text-red-600" /></button>
                </>
              )}
              {canPropose(s, isSupplier) && (
                <>
                  <button
                    onClick={() => onOpenModal('propose_date', s)}
                    className="p-1 hover:bg-blue-50 rounded"
                    title="Propose Date Change"
                    type="button"
                  >
                    <CalendarDays className="h-4 w-4 text-blue-600" />
                  </button>
                  <button
                    onClick={() => onOpenModal('propose_qty', s)}
                    className="p-1 hover:bg-purple-50 rounded"
                    title="Propose Qty Change"
                    type="button"
                  >
                    <Hash className="h-4 w-4 text-purple-600" />
                  </button>
                </>
              )}
              {!isCollabClosed(s) && (
                s.on_hold ? (
                  mayReleaseHold(viewerSide, s) ? (
                    <button onClick={() => onOpenModal('release', s)} className="p-1 hover:bg-green-50 rounded" title="Release Hold"><Play className="h-4 w-4 text-green-600" /></button>
                  ) : (
                    <span
                      className="p-1 text-muted-foreground"
                      title={statusOf(s, ACK_STATUS_CONFIG, SCHEDULE_CLOSED_RECEIPT_STATUSES).title}
                    >
                      <Pause className="h-4 w-4 opacity-40" />
                    </span>
                  )
                ) : (
                  <button onClick={() => onOpenModal('hold', s)} className="p-1 hover:bg-orange-50 rounded" title="Put On Hold"><Pause className="h-4 w-4 text-orange-600" /></button>
                )
              )}
              {canCloseHeld(s, isSupplier) && (
                <button
                  onClick={() => onOpenModal('close_held', s)}
                  className="p-1 hover:bg-violet-50 rounded"
                  title="Close line — manual agreement remainder (receipts kept)"
                  type="button"
                  data-testid="close-held-btn"
                >
                  <CircleSlash className="h-4 w-4 text-violet-700" />
                </button>
              )}
              {(isCollabClosed(s) || (!canAct(s, isSupplier) && !canPropose(s, isSupplier) && !s.on_hold && !canCloseHeld(s, isSupplier))) && (
                <span className="text-xs text-muted-foreground">-</span>
              )}
            </div>
          </td>
        );
      // RETIRED 17 Sep 2026 (Aaron): the schedule grain's 'on_hold' column goes
      // the way the PO line grid's did — a held row's STATUS is the hold, and a
      // column repeating it is how two badges come to disagree. The hold reason
      // stays in `exception_reason`, which is a different fact (the why).
      case 'exception_reason': {
        // Each part gets its own chip so "Date Change + On Hold" reads as two
        // facts rather than one long string.
        const parts = describeExceptionReason(s).split(' + ').filter(Boolean);
        return (
          <td key={colId} className="py-3 px-4">
            {parts.length === 0 ? (
              <span className="text-muted-foreground text-xs">-</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {parts.map((part) => (
                  <span
                    key={part}
                    className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      part === 'On Hold' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {part}
                  </span>
                ))}
              </div>
            )}
          </td>
        );
      }
      case 'on_hold_reason':
        return (
          <td key={colId} className="py-3 px-4 text-sm">
            {s.on_hold && s.on_hold_reason_text ? (
              // Held lines often carry a paragraph; clamp it and keep the full
              // text on hover so the row height stays uniform.
              <span className="line-clamp-2" title={s.on_hold_reason_text}>
                {s.on_hold_reason_text}
              </span>
            ) : s.on_hold ? (
              <span className="text-muted-foreground text-xs italic">No reason given</span>
            ) : (
              <span className="text-muted-foreground text-xs">-</span>
            )}
          </td>
        );
      case 'waiting_on':
        return <td key={colId} className="py-3 px-4 text-center">{renderWaitingOn(s)}</td>;
      case 'po_number':
        // CR-1154: PO number links to the PO header; line to the line item.
        return <td key={colId} className="py-3 px-4"><PoHeaderLink poLineId={s.po_line_id} poNumber={s.po_number} className="text-primary hover:underline"><span className="font-medium text-sm">{s.po_number}</span></PoHeaderLink></td>;
      case 'line_number':
        // CR-1154: deep-link to the PO line item detail.
        return <td key={colId} className="py-3 px-4 text-center"><PoLineLink poLineId={s.po_line_id} className=""><span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-xs font-medium hover:underline">{s.line_number}</span></PoLineLink></td>;
      case 'schedule_line_number':
        return <td key={colId} className="py-3 px-4 text-center text-sm">{s.schedule_line_number || '-'}</td>;
      case 'erp_order_type':
        return (
          <td key={colId} className="py-3 px-4 text-sm">
            {s.erp_order_type
              ? <span className="px-2 py-1 bg-muted rounded text-xs font-medium" title={ERP_ORDER_TYPE_LABELS[s.erp_order_type] || `ERP type ${s.erp_order_type}`}>{s.erp_order_type}</span>
              : <span className="text-muted-foreground text-xs">-</span>}
          </td>
        );
      case 'part_number':
        return <td key={colId} className="py-3 px-4"><span className="font-mono text-sm font-medium">{s.part_number}</span></td>;
      case 'supplier_part_number':
        return <td key={colId} className="py-3 px-4"><span className="font-mono text-sm">{s.supplier_part_number || '-'}</span></td>;
      case 'buyer_code':
        return <td key={colId} className="py-3 px-4"><span className="font-mono text-sm">{s.buyer_code || '-'}</span></td>;
      case 'part_description':
        // CR-7308: the column beside Part Number is the PART MASTER's name for
        // the part, never the text typed on the order line.
        return <td key={colId} className="py-3 px-4"><div className="text-sm">{s.part_description || '—'}</div></td>;
      case 'supplier_name':
        return (
          <td key={colId} className="py-3 px-4 text-sm">
            {s.supplier_id ? (
              <Link
                to={`/suppliers/${s.supplier_id}`}
                className="text-primary hover:underline inline-flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                {s.supplier_name || 'N/A'}
                <ExternalLink className="h-3 w-3" />
              </Link>
            ) : (
              s.supplier_name || 'N/A'
            )}
          </td>
        );
      case 'warehouse':
        return <td key={colId} className="py-3 px-4 text-sm">{s.warehouse || '—'}</td>;
      case 'customer_name':
        return <td key={colId} className="py-3 px-4 text-sm">{s.customer_name || 'N/A'}</td>;
      case 'schedule_date':
        return <td key={colId} className="py-3 px-4 text-sm">{s.schedule_date ? formatDate(s.schedule_date) : '-'}</td>;
      case 'supplier_promise_date':
        return <td key={colId} className="py-3 px-4 text-sm">{s.supplier_promise_date ? formatDate(s.supplier_promise_date) : '-'}</td>;
      case 'original_promise_date':
        return <td key={colId} className="py-3 px-4 text-sm">{s.original_required_date ? formatDate(s.original_required_date) : '-'}</td>;
      case 'schedule_qty':
        return <td key={colId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{formatNumber(parseFloat(s.schedule_qty))}</span></td>;
      case 'shipped_qty':
        return <td key={colId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{formatNumber(parseFloat(s.shipped_qty))}</span></td>;
      case 'balance_qty': {
        const bal = parseFloat(s.balance_qty);
        return <td key={colId} className="py-3 px-4 text-right"><span className={`text-sm font-medium ${bal > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>{formatNumber(bal)}</span></td>;
      }
      case 'erp_balance_qty':
        return <td key={colId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{s.erp_balance_qty != null ? formatNumber(parseFloat(s.erp_balance_qty)) : '—'}</span></td>;
      case 'unit_of_measure':
        return <td key={colId} className="py-3 px-4 text-sm text-center"><span className="px-2 py-1 bg-muted rounded text-xs font-medium">{s.unit_of_measure || '-'}</span></td>;
      case 'unit_price':
        return <td key={colId} className="py-3 px-4 text-right text-sm">{s.unit_price ? parseFloat(s.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }) : '-'}</td>;
      case 'status':
        return <td key={colId} className="py-3 px-4">{renderStatusBadge(s)}</td>;
      case 'updated_at':
        return <td key={colId} className="py-3 px-4 text-sm">{s.updated_at ? formatDate(s.updated_at) : '-'}</td>;
      default:
        return null;
    }
  };

  const closed = isClosed(s);
  const forecast = !!s.is_forecast;
  return (
    <tr
      className={`border-b hover:bg-muted/50 transition-colors cursor-pointer ${s.on_hold ? 'bg-red-50/50' : ''} ${closed ? 'opacity-60 bg-muted/40' : ''} ${forecast ? 'bg-amber-50/50' : ''}`}
      onClick={() => onRowClick?.(s.id)}
      title={forecast ? `Forecast — outside firm window (${s.firm_days ?? '?'} days)` : undefined}
    >
      <td className="py-2 px-3 sticky left-0 bg-background z-10" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(s.id)}
          disabled={!canSelectRow}
          className="h-4 w-4 rounded border-gray-300"
        />
      </td>
      {visibleColumns.map((col) => renderCell(col.id))}
    </tr>
  );
}
